import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../../config/axiosConfig';
import { API_URLS, TIMEZONES } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { Modal, ModalTitle, ModalContent, ModalActions } from '../basic';

interface AddClientModalProps {
  onClose: () => void;
  onClientAdded?: () => void;
  editClient?: any | null;
}

// Zod validation schema
const clientSchema = z.object({
  name: z.string().min(1, 'Name is required').trim(),
  address: z
    .string()
    .optional()
    .default('')
    .transform((v) => v || ''),
  city: z
    .string()
    .optional()
    .default('')
    .transform((v) => v || ''),
  state: z
    .string()
    .optional()
    .default('')
    .transform((v) => v || ''),
  zip: z
    .string()
    .optional()
    .default('')
    .transform((v) => v || ''),
  active: z.boolean(),
  freeGamesLimit: z.number().int().min(0).optional(),
  lead: z
    .object({
      firstName: z.string().min(1, 'First name is required').trim(),
      lastName: z.string().min(1, 'Last name is required').trim(),
      email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
      phone: z
        .string()
        .optional()
        .default('')
        .transform((v) => v || ''),
      timezone: z.string().min(1, 'Timezone is required'),
      role: z.string().min(1, 'Please select a role for the lead user'),
    })
    .optional(),
});
const AddClientModal: React.FC<AddClientModalProps> = ({ onClose, onClientAdded, editClient }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [roles, setRoles] = useState<any[]>([]);

  // Initialize react-hook-form with zod validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: '',
      address: undefined,
      city: undefined,
      state: undefined,
      zip: undefined,
      active: true,
      lead: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        timezone: 'eastern',
        role: '',
      },
    },
  });

  // Fetch roles on component mount
  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get(API_URLS.GET_USER_ROLES);
      if (response.data.success && response.data.data) {
        setRoles(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  // Pre-fill form if editing
  useEffect(() => {
    if (editClient) {
      reset({
        name: editClient.name || '',
        address: editClient.address || '',
        city: editClient.city || '',
        state: editClient.state || '',
        zip: editClient.zip || '',
        active: !!editClient.active,
        freeGamesLimit: editClient.freeGamesLimit ?? undefined,
        ...(editClient.lead && {
          lead: {
            firstName: editClient.lead?.firstName || '',
            lastName: editClient.lead?.lastName || '',
            email: editClient.lead?.email || '',
            phone: editClient.lead?.phone || '',
            timezone: editClient.lead?.timezone || 'eastern',
            role: editClient.lead?.role || '',
          },
        }),
      });
      // Set existing logo preview if available
      setSelectedImage(
        editClient.image ? `${process.env.REACT_APP_UPLOAD_URL}uploads/clients/${editClient.image}` : null,
      );
    }
  }, [editClient, reset]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);

    // Create preview
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedImage(null);
    }
  };

  const handleClose = () => {
    if (!isLoading && !isSubmitting) {
      onClose();
    }
  };

  const onSubmit = async (data: any) => {
    const token = sessionManager.getToken();
    if (!token) {
      showErrorMsg('Authentication token not found. Please log in again.');
      return;
    }

    // Logo is optional for both creating and editing clients

    setIsLoading(true);

    try {
      let response;
      if (editClient && editClient._id) {
        // Update client
        const updateData: any = { ...data };

        // If a new logo is selected, handle file upload with FormData
        if (logoFile) {
          const submitData = new FormData();
          submitData.append('image', logoFile);
          submitData.append('_id', editClient._id); // Add client ID
          ['name', 'address', 'city', 'state', 'zip', 'active', 'freeGamesLimit'].forEach((k) => {
            if (typeof updateData[k] !== 'undefined') {
              submitData.append(k, String(updateData[k]));
            }
          });
          if (updateData.lead) {
            // Include original email so backend can match existing user if email changed
            if (editClient.lead?.email) {
              submitData.append('lead[originalEmail]', editClient.lead.email);
            }
            Object.entries(updateData.lead).forEach(([k, v]) => {
              submitData.append(`lead[${k}]`, String(v));
            });
          }
          response = await api.put(API_URLS.UPDATE_CLIENT, submitData);
        } else {
          updateData._id = editClient._id; // Add client ID to JSON data
          if (updateData.lead && editClient.lead?.email) {
            updateData.lead.originalEmail = editClient.lead.email;
          }
          response = await api.put(API_URLS.UPDATE_CLIENT, updateData);
        }
        if (response.data.success) {
          showSuccessMsg('Client updated successfully!');
          onClientAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to update client');
        }
      } else {
        // Add client and send invite to lead contact
        if (!data.lead) {
          showErrorMsg('Lead contact information is required for new clients');
          return;
        }

        // If a logo is selected, use FormData; otherwise use JSON
        if (logoFile) {
          const formData = new FormData();
          formData.append('image', logoFile);
          formData.append('clientName', data.name);
          if (data.address) formData.append('address', data.address);
          if (data.city) formData.append('city', data.city);
          if (data.state) formData.append('state', data.state);
          if (data.zip) formData.append('zip', data.zip);
          formData.append('lead[firstName]', data.lead.firstName);
          formData.append('lead[lastName]', data.lead.lastName);
          formData.append('lead[email]', data.lead.email);
          formData.append('lead[phone]', data.lead.phone || '');
          formData.append('lead[timezone]', data.lead.timezone);
          formData.append('lead[role]', data.lead.role || '');

          response = await api.post(API_URLS.ADD_CLIENT_WITH_INVITE, formData);
        } else {
          // No logo file, send JSON data
          const payload = {
            clientName: data.name,
            address: data.address || '',
            city: data.city || '',
            state: data.state || '',
            zip: data.zip || '',
            lead: {
              firstName: data.lead.firstName,
              lastName: data.lead.lastName,
              email: data.lead.email,
              phone: data.lead.phone,
              timezone: data.lead.timezone,
              role: data.lead.role,
            },
          };
          response = await api.post(API_URLS.ADD_CLIENT_WITH_INVITE, payload);
        }
        if (response.data.success) {
          showSuccessMsg('Client created and invitation sent!');
          onClientAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to add client');
        }
      }
    } catch (error: any) {
      console.error('Error adding client:', error);

      // Handle different types of errors
      if (error.response?.status === 401) {
        showErrorMsg('Session expired. Please log in again.');
      } else if (error.response?.data?.message) {
        showErrorMsg(error.response.data.message);
      } else if (error.message) {
        showErrorMsg(`Error: ${error.message}`);
      } else {
        showErrorMsg('Failed to add client. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} maxWidth="2xl" maxHeight="90vh" isLoading={isLoading || isSubmitting}>
      <ModalTitle>{editClient ? 'Edit Client' : 'Add Client'}</ModalTitle>

      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)} id="client-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-2">Client Information</h3>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Name *</label>
                <input
                  type="text"
                  {...register('name')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Address</label>
                <input
                  type="text"
                  {...register('address')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.address ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address.message}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">City</label>
                <input
                  type="text"
                  {...register('city')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.city ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city.message}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">State</label>
                <input
                  type="text"
                  {...register('state')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.state ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state.message}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">ZIP Code</label>
                <input
                  type="text"
                  {...register('zip')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.zip ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.zip && <p className="text-red-500 text-sm mt-1">{errors.zip.message}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">School Logo</label>
                <input type="file" accept="image/*" onChange={handleFileChange} disabled={isLoading || isSubmitting} />
                {editClient ? (
                  <p className="text-gray-500 text-sm mt-1">Leave empty to keep the existing logo</p>
                ) : (
                  <p className="text-gray-500 text-sm mt-1">Upload a logo for the client (optional)</p>
                )}
              </div>

              {selectedImage && (
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-1">Logo Preview</label>
                  <div className="w-full h-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-gray-50">
                    <img src={selectedImage} alt="Logo preview" className="max-w-full max-h-full object-contain" />
                  </div>
                </div>
              )}

              <div className="mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="active"
                    {...register('active')}
                    className="mr-2"
                    disabled={isLoading || isSubmitting}
                  />
                  <label htmlFor="active" className="text-gray-700">
                    Active
                  </label>
                </div>
              </div>

              {editClient && (
                <div className="mb-4 border border-gray-200 rounded p-3 bg-gray-50">
                  <h4 className="font-semibold text-gray-700 mb-3">Free Games (Trial)</h4>
                  <div>
                    <label className="block text-gray-700 font-medium mb-1 text-sm">Games Limit</label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      {...register('freeGamesLimit', { valueAsNumber: true })}
                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                        errors.freeGamesLimit ? 'border-red-500' : 'border-gray-300'
                      }`}
                      disabled={isLoading || isSubmitting}
                      placeholder="e.g. 3"
                    />
                    {errors.freeGamesLimit && (
                      <p className="text-red-500 text-sm mt-1">{(errors.freeGamesLimit as any).message}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <h3 className="font-semibold mb-2">Lead Account Contact Information</h3>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">First Name *</label>
                <input
                  type="text"
                  {...register('lead.firstName')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.lead?.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.lead?.firstName && (
                  <p className="text-red-500 text-sm mt-1">{(errors.lead.firstName as any).message}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Last Name *</label>
                <input
                  type="text"
                  {...register('lead.lastName')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.lead?.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.lead?.lastName && (
                  <p className="text-red-500 text-sm mt-1">{(errors.lead.lastName as any).message}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Email *</label>
                <input
                  type="email"
                  {...register('lead.email')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.lead?.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.lead?.email && (
                  <p className="text-red-500 text-sm mt-1">{(errors.lead.email as any).message}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Phone</label>
                <input
                  type="tel"
                  {...register('lead.phone')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.lead?.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                />
                {errors.lead?.phone && (
                  <p className="text-red-500 text-sm mt-1">{(errors.lead.phone as any).message}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Time Zone *</label>
                <select
                  {...register('lead.timezone')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.lead?.timezone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading || isSubmitting}
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
                {errors.lead?.timezone && (
                  <p className="text-red-500 text-sm mt-1">{(errors.lead.timezone as any).message}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">User Role *</label>
                <div className="space-y-2">
                  {roles
                    .filter((role) => role.type === 'client')
                    .map((role) => (
                      <div key={role._id} className="flex items-center">
                        <input
                          type="radio"
                          id={`lead-role-${role._id}`}
                          value={role._id}
                          {...register('lead.role')}
                          className="mr-2"
                          disabled={isLoading || isSubmitting}
                        />
                        <label htmlFor={`lead-role-${role._id}`} className="text-gray-700">
                          {role.label}
                        </label>
                      </div>
                    ))}
                </div>
                {errors.lead?.role && <p className="text-red-500 text-sm mt-1">{errors.lead.role.message}</p>}
              </div>
            </div>
          </div>
        </form>
      </ModalContent>

      <ModalActions>
        <button
          type="button"
          onClick={handleClose}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors disabled:opacity-50"
          disabled={isLoading || isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="client-form"
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          disabled={isLoading || isSubmitting}
        >
          {isLoading || isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {editClient ? 'Saving...' : 'Adding...'}
            </>
          ) : editClient ? (
            'Save Changes'
          ) : (
            'Send Invitation to STATDOG'
          )}
        </button>
      </ModalActions>
    </Modal>
  );
};

export default AddClientModal;
